import { pinballAudio } from './proceduralAudio';
import {
  SOUND_EFFECTS,
  defaultSoundMap,
  type SoundEffectId,
} from './creatorPacks';
import { nextSoundVariant } from './soundVariations';
import type { GameEvent } from '../engine/engine';
import type { TableDefinition, TableSoundEvent } from '../engine/tables/definition';

const EVENT_TYPES = new Set<TableSoundEvent>([
  'game-start',
  'flipper',
  'bumper',
  'slingshot',
  'target',
  'rollover',
  'ramp',
  'plunger',
  'nudge',
  'drain',
  'ball-save',
  'tilt',
  'game-over',
  'jackpot',
  'prop-appear',
  'prop-destroy',
]);

const SOUND_EFFECT_IDS = new Set<string>(SOUND_EFFECTS.map((effect) => effect.id));

const HAUNTED_KEEP_SIGNATURE_SOUNDS: Readonly<Record<string, SoundEffectId>> = {
  // The spirit trap is Haunted Keep's signature catch. Give the capture itself
  // an authored supernatural draw before the guardian gauntlet resumes.
  'catch-haunted:target': 'spectral-capture',
  'target-1:target': 'coil-heavy',
  'target-1:prop-destroy': 'pop-deep',
  'target-2:target': 'lane-bleep',
  'target-2:prop-destroy': 'save-rise',
  'target-3:target': 'pop-deep',
  'target-3:prop-destroy': 'jackpot-fanfare',
  'ramp-main:ramp': 'coil-heavy',
  'ramp-upper-loop:ramp': 'ramp-bell',
};

const CLOCKWORK_DISTRICT_SIGNATURE_SOUNDS: Readonly<Record<string, SoundEffectId>> = {
  // Upper lanes should sound like three teeth in the same escapement rather
  // than generic rollover traffic: tick, spring advance, then bell release.
  'roll-a:rollover': 'coil-clack',
  'roll-b:rollover': 'plunger-spring',
  'roll-c:rollover': 'ramp-bell',
  // Pressure Test is a nine-hit mission. Give the three pressure stations
  // separate mechanical voices so repeated bumper work reads left-to-right by ear.
  'bumper-left:bumper': 'coil-clack',
  'bumper-center:bumper': 'plunger-spring',
  'bumper-right:bumper': 'pop-deep',
  // Recovery hardware joins the mechanism rhythm: a heavier escapement snap on
  // the left, then a spring-loaded recoil on the right.
  'sling-left:slingshot': 'coil-heavy',
  'sling-right:slingshot': 'plunger-spring',
  // The catch itself keeps its authored heavy-coil override. The automaton now
  // owns a paired pallet tick before its spring releases; the mechanism ramps
  // then continue through drive gear and bell/governor return.
  'target-2:target': 'escapement-tick',
  'target-2:prop-destroy': 'plunger-spring',
  'ramp-main:ramp': 'coil-heavy',
  'ramp-upper-loop:ramp': 'ramp-bell',
};

const DEEP_SEA_DIVE_SIGNATURE_SOUNDS: Readonly<Record<string, SoundEffectId>> = {
  // Sonar Sweep now reads as transmit -> deep-water return -> contact instead
  // of three generic lane tones, while preserving the left-to-right scan.
  'roll-a:rollover': 'lane-ping',
  'roll-b:rollover': 'sonar-return',
  'roll-c:rollover': 'target-zap',
  // Reef Rescue is a ten-hit mission, so make its three spatially separated
  // bumpers readable by ear: low pressure pop -> sonar bleep -> electric snap.
  // The recovery slings continue that underwater machinery language with a
  // heavy ballast recoil on the left and a sonar return pulse on the right.
  // The catch then leads into the heavier lock/release and trench sweep arc.
  'bumper-left:bumper': 'pop-deep',
  'bumper-center:bumper': 'lane-bleep',
  'bumper-right:bumper': 'target-zap',
  'sling-left:slingshot': 'coil-heavy',
  'sling-right:slingshot': 'sonar-return',
  'target-2:target': 'coil-heavy',
  'target-2:prop-destroy': 'save-rise',
  'ramp-main:ramp': 'ramp-rise',
};

const COSMIC_RELAY_SIGNATURE_SOUNDS: Readonly<Record<string, SoundEffectId>> = {
  // Treat the three top lanes as an uplink-acquisition pass rather than generic
  // rollover traffic: beacon ping -> carrier lock -> transmission climb.
  'roll-a:rollover': 'lane-ping',
  'roll-b:rollover': 'target-zap',
  'roll-c:rollover': 'ramp-rise',
  // Meteor Storm is a six-hit survival loop. Give its three impact zones
  // separate deep-space voices so repeated collisions are spatially readable.
  'bumper-left:bumper': 'pop-deep',
  'bumper-center:bumper': 'target-zap',
  'bumper-right:bumper': 'lane-bleep',
  // Lower recovery should reinforce the table's orbit/uplink language: the
  // left side lands as a heavy attitude-control kick while the right answers
  // with an ascending thruster sweep back toward the relay field.
  'sling-left:slingshot': 'coil-heavy',
  'sling-right:slingshot': 'ramp-rise',
  // Relay Surge keeps its authored target-zap catch cue. The three one-hit
  // relay completions now open with a dedicated telemetry handshake before an
  // uplink rise and full transmission reward, mirroring the ordered chain.
  'target-1:prop-destroy': 'telemetry-uplink',
  'target-2:prop-destroy': 'save-rise',
  'target-3:prop-destroy': 'jackpot-fanfare',
};

const FRONTIER_RUN_SIGNATURE_SOUNDS: Readonly<Record<string, SoundEffectId>> = {
  // Gold Rush opens on the mine hardware; give each mine station a different
  // cabinet voice before the ordered wanted-bank and railway cues.
  'bumper-left:bumper': 'target-knock',
  'bumper-center:bumper': 'pop-deep',
  'bumper-right:bumper': 'ramp-bell',
  // Load the Ore Cars closes the mine-to-freight arc through all three top lanes.
  // Make each car latch sound progressively heavier as the freight consist fills.
  'roll-a:rollover': 'lane-ping',
  'roll-b:rollover': 'coil-clack',
  'roll-c:rollover': 'ramp-bell',
  // Lower recovery should sound like two different pieces of frontier hardware:
  // a dry rawhide/rubber snap on the left and a telegraph-like relay clack on the right.
  'sling-left:slingshot': 'rubber-snap',
  'sling-right:slingshot': 'coil-clack',
  // The wanted bank is an ordered one-hit chase. Make each outlaw land harder
  // than the last, then separate the outbound rail shot from the return bell.
  'target-1:prop-destroy': 'target-knock',
  'target-2:prop-destroy': 'coil-heavy',
  'target-3:prop-destroy': 'jackpot-fanfare',
  'ramp-main:ramp': 'coil-heavy',
  'ramp-upper-loop:ramp': 'ramp-bell',
};

const DRAGON_KEEP_SIGNATURE_SOUNDS: Readonly<Record<string, SoundEffectId>> = {
  // Give the three everyday keep bumpers separate material voices so routine
  // combat reads by position: battlement knock -> deep courtyard hit -> tower chime.
  'bumper-left:bumper': 'target-knock',
  'bumper-center:bumper': 'pop-deep',
  'bumper-right:bumper': 'pop-chime',
  // Recovery hits should also carry direction: the left sling is a shield-like
  // metal snap while the right answers with a brighter tower-bell rebound.
  'sling-left:slingshot': 'coil-clack',
  'sling-right:slingshot': 'ramp-bell',
  // The boss gauntlet should sound like a staged siege rather than three copies
  // of the same fantasy hit: stone breach -> dragon breath -> crystal shatter ->
  // guardian defeat, then a rising launch and bell-like upper-flight completion.
  'target-1:target': 'coil-heavy',
  'target-1:prop-destroy': 'pop-deep',
  'target-2:target': 'dragon-breath',
  'target-2:prop-destroy': 'save-rise',
  'target-3:target': 'coil-heavy',
  'target-3:prop-destroy': 'jackpot-fanfare',
  'ramp-main:ramp': 'ramp-rise',
  'ramp-upper-loop:ramp': 'ramp-bell',
};

const JUNGLE_TEMPLE_SIGNATURE_SOUNDS: Readonly<Record<string, SoundEffectId>> = {
  // Relic Chamber is a seven-hit ritual. Give its three jungle pop stations
  // separate stone/chime voices so repeated relic work is spatially readable.
  'bumper-left:bumper': 'target-knock',
  'bumper-center:bumper': 'pop-chime',
  'bumper-right:bumper': 'lane-ping',
  // Lower recovery should carry direction too: a dry vine/rubber snap on the
  // left and a bright temple bell rebound on the right.
  'sling-left:slingshot': 'rubber-snap',
  'sling-right:slingshot': 'ramp-bell',
  // The relic catch keeps its authored stone knock. Give the revealed heart its
  // own stone-and-crystal pulse before the existing release flourish, while the
  // Waterfall Run finishes the ritual with a distinct rising traversal cue.
  'target-2:target': 'temple-heart',
  'target-2:prop-destroy': 'save-rise',
  'ramp-main:ramp': 'ramp-rise',
};

const GRAND_CIRCUIT_SIGNATURE_SOUNDS: Readonly<Record<string, SoundEffectId>> = {
  // Light the Grid is a three-lane top rollover set. Give each lane a distinct
  // checkpoint voice so the left-to-right sequence reads by ear instead of one
  // repeated generic lane ping.
  'roll-a:rollover': 'lane-ping',
  'roll-b:rollover': 'lane-bleep',
  'roll-c:rollover': 'pop-chime',
  // Give the recovery lane a directional pit-wall feel: a tight suspension snap
  // on the left and a brighter marshal/bell response on the right.
  'sling-left:slingshot': 'coil-clack',
  'sling-right:slingshot': 'ramp-bell',
  // Pit Service already has an authored bell catch. Give the three finish-line
  // bays distinct mechanical service hits, then make the Grand Loop sound like
  // an accelerating run rather than another generic plastic-ramp impact.
  'target-1:target': 'coil-clack',
  'target-2:target': 'target-knock',
  'target-3:target': 'coil-heavy',
  'ramp-main:ramp': 'ramp-rise',
};

const NEON_RUSH_SIGNATURE_SOUNDS: Readonly<Record<string, SoundEffectId>> = {
  // The three upper lanes now read as a voltage ramp rather than three copies of
  // the default rollover bleep: low-voltage acknowledgement -> arc contact ->
  // full bus rise. That gives ordinary top-lane routing a Neon-specific cadence.
  'roll-a:rollover': 'lane-bleep',
  'roll-b:rollover': 'target-zap',
  'roll-c:rollover': 'ramp-rise',
  // Charge Reactor is an eight-hit objective. Give its three hot-pop stations
  // distinct electronic voices so repeated reactor work is readable by ear.
  'bumper-left:bumper': 'lane-bleep',
  'bumper-center:bumper': 'target-zap',
  'bumper-right:bumper': 'pop-deep',
  // Keep fast lower-playfield recovery directional: the left side lands as a
  // heavy power-relay snap while the right answers with a short electronic pulse.
  'sling-left:slingshot': 'coil-heavy',
  'sling-right:slingshot': 'lane-bleep',
  // Overdrive already owns a rising launch cue. Let the sequential one-shot
  // breakers escalate from an armed-state confirmation into a dedicated
  // high-voltage contactor trip, then a full limit-break reward.
  'target-1:prop-destroy': 'lane-bleep',
  'target-2:prop-destroy': 'reactor-breaker',
  'target-3:prop-destroy': 'jackpot-fanfare',
};

const CANDY_CARNIVAL_SIGNATURE_SOUNDS: Readonly<Record<string, SoundEffectId>> = {
  // The three Sugar Rush lanes trade duration for value. Make that risk curve
  // audible too: a gentle entry ping, a brighter middle chime, then a rising
  // reward cue on the shortest/highest-value lane.
  'roll-a:rollover': 'lane-ping',
  'roll-b:rollover': 'pop-chime',
  'roll-c:rollover': 'save-rise',
  // Candy Frenzy is a six-hit celebration. Make the three pop stations read as
  // separate carnival toys while keeping the table's deliberately silly palette.
  'bumper-left:bumper': 'boing',
  'bumper-center:bumper': 'fart-pop',
  'bumper-right:bumper': 'chicken-squawk',
  // Lower recovery should feel like two different carnival mechanisms: a
  // springy toy rebound on the left and a bright prize-bell rebound on the right.
  'sling-left:slingshot': 'boing',
  'sling-right:slingshot': 'pop-chime',
  // Sugar Rush is a catch-gated reward cascade: capture the ball, expose the
  // center jackpot, then spill into two side prizes. Keep the authored catch
  // cue intact and make the revealed rewards climb from candy-pop confirmation
  // to a full jackpot, followed by bright prize pickups and a slide bell.
  'target-2:target': 'lane-bleep',
  'target-2:prop-destroy': 'jackpot-fanfare',
  'target-1:prop-destroy': 'save-rise',
  'target-3:prop-destroy': 'save-rise',
  'ramp-main:ramp': 'ramp-bell',
};

interface AudioTableIndex {
  flipperLeftId?: string;
  flipperRightId?: string;
  plungerId?: string;
  drainId?: string;
  partSoundRefs: Map<string, string>;
  defaultEffects: Record<TableSoundEvent, SoundEffectId>;
}

const AUDIO_TABLE_INDEX = new WeakMap<TableDefinition, AudioTableIndex>();

function audioTableIndex(table: TableDefinition): AudioTableIndex {
  const cached = AUDIO_TABLE_INDEX.get(table);
  if (cached) return cached;

  const index: AudioTableIndex = {
    partSoundRefs: new Map<string, string>(),
    defaultEffects: defaultSoundMap(table.theme.soundPackId),
  };

  for (const part of table.parts) {
    if (part.soundRef) index.partSoundRefs.set(part.id, part.soundRef);
    if (part.kind === 'flipper-left' && index.flipperLeftId === undefined) index.flipperLeftId = part.id;
    else if (part.kind === 'flipper-right' && index.flipperRightId === undefined) index.flipperRightId = part.id;
    else if (part.kind === 'plunger-lane' && index.plungerId === undefined) index.plungerId = part.id;
    else if (part.kind === 'drain' && index.drainId === undefined) index.drainId = part.id;
  }

  AUDIO_TABLE_INDEX.set(table, index);
  return index;
}

function partIdFor(event: GameEvent, index: AudioTableIndex): string | undefined {
  if ('partId' in event) return event.partId;
  if (event.type === 'flipper') return event.side === 'left' ? index.flipperLeftId : index.flipperRightId;
  if (event.type === 'plunger') return index.plungerId;
  if (event.type === 'drain') return index.drainId;
  return undefined;
}

function soundEventFor(event: GameEvent): TableSoundEvent | null {
  if (event.type === 'target-activated') return 'prop-appear';
  if (event.type === 'target' && event.destroyed) return 'prop-destroy';
  if (event.type === 'kicker') return 'target';
  return EVENT_TYPES.has(event.type as TableSoundEvent) ? event.type as TableSoundEvent : null;
}

function builtInSignatureSound(
  table: TableDefinition,
  partId: string | undefined,
  eventType: TableSoundEvent,
): SoundEffectId | undefined {
  if (!partId) return undefined;

  const key = `${partId}:${eventType}`;
  if (table.theme.machineArtId === 'haunted-keep') return HAUNTED_KEEP_SIGNATURE_SOUNDS[key];
  if (table.theme.machineArtId === 'clockwork-district') return CLOCKWORK_DISTRICT_SIGNATURE_SOUNDS[key];
  if (table.theme.machineArtId === 'deep-sea-dive') return DEEP_SEA_DIVE_SIGNATURE_SOUNDS[key];
  if (table.theme.machineArtId === 'cosmic-relay') return COSMIC_RELAY_SIGNATURE_SOUNDS[key];
  if (table.theme.machineArtId === 'frontier-run') return FRONTIER_RUN_SIGNATURE_SOUNDS[key];
  if (table.theme.machineArtId === 'dragon-keep') return DRAGON_KEEP_SIGNATURE_SOUNDS[key];
  if (table.theme.machineArtId === 'jungle-temple') return JUNGLE_TEMPLE_SIGNATURE_SOUNDS[key];
  if (table.theme.machineArtId === 'neon-rush') return NEON_RUSH_SIGNATURE_SOUNDS[key];
  if (table.theme.machineArtId === 'candy-carnival') return CANDY_CARNIVAL_SIGNATURE_SOUNDS[key];
  if (table.id === 'grand-circuit-v1') return GRAND_CIRCUIT_SIGNATURE_SOUNDS[key];
  return undefined;
}

function explicitSoundFor(
  table: TableDefinition,
  index: AudioTableIndex,
  partId: string | undefined,
  eventType: TableSoundEvent,
): SoundEffectId | undefined {
  const partOverride = partId ? index.partSoundRefs.get(partId) : undefined;
  if (partOverride && SOUND_EFFECT_IDS.has(partOverride)) return partOverride as SoundEffectId;

  const signature = builtInSignatureSound(table, partId, eventType);
  if (signature) return signature;

  const eventOverride = table.theme.soundEventMap?.[eventType];
  if (eventOverride && SOUND_EFFECT_IDS.has(eventOverride)) return eventOverride as SoundEffectId;
  return undefined;
}

export function resolveGameEventSound(event: GameEvent, table: TableDefinition): SoundEffectId | null {
  const eventType = soundEventFor(event);
  if (!eventType) return null;

  const index = audioTableIndex(table);
  const partId = partIdFor(event, index);
  return explicitSoundFor(table, index, partId, eventType) ?? index.defaultEffects[eventType];
}

export function playGameEventAudio(event: GameEvent, table: TableDefinition): void {
  const eventType = soundEventFor(event);
  if (!eventType) return;

  const index = audioTableIndex(table);
  const partId = partIdFor(event, index);
  const explicit = explicitSoundFor(table, index, partId, eventType);
  const primary = explicit ?? index.defaultEffects[eventType];
  if (!primary) return;

  const effect = explicit
    ? primary
    : nextSoundVariant(table.theme.soundPackId, eventType, primary);

  try {
    pinballAudio.playEffect(effect, table.theme.soundPackId);
  } catch {
    // Audio is optional; a closed browser audio context must never interrupt play.
  }
}
